"use client";

import { SearchNormal1 } from "iconsax-reactjs";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import type React from "react";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { SEARCH_MOTION_EASE, SEARCH_MOTION_TRANSITION } from "./search-config";

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  isExpanded: boolean;
  onCancel: () => void;
}

export const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  isExpanded,
  onCancel,
}) => {
  const t = useTranslations("nav.search");
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <motion.div
      className="relative flex items-center gap-2"
      initial={false}
      animate={{ flex: isExpanded ? "1 1 auto" : "0 1 auto" }}
      transition={SEARCH_MOTION_TRANSITION}
      style={{ minWidth: 0 }}
    >
      {/* Input container */}
      <div className="relative w-full">
        <Input
          ref={inputRef}
          className={cn("rounded-full", !isExpanded && "ps-4")}
          type="text"
          placeholder={t("placeholder")}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          // r2: pass `null` (NOT `undefined`) when slot is empty.
          // Input.tsx:12-13 hasAdornments treats null as truthy →
          // wrapped-<div> DOM branch stays mounted across the
          // isExpanded flip → input element identity is preserved →
          // focus does not drop mid-typing on the 3-char threshold.
          // Input.tsx:67-72 padding ternary resolves null → ps-3.
          startAdornment={
            isExpanded ? (
              <motion.div
                key="start-icon"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2, ease: SEARCH_MOTION_EASE }}
              >
                <SearchNormal1
                  className="text-text-primary"
                  size={20}
                  variant="Linear"
                />
              </motion.div>
            ) : null
          }
          endAdornment={
            !isExpanded ? (
              <motion.div
                key="end-icon"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2, ease: SEARCH_MOTION_EASE }}
              >
                <SearchNormal1
                  className="text-text-secondary"
                  size={20}
                  variant="Linear"
                />
              </motion.div>
            ) : null
          }
        />
      </div>

      {/* Cancel button — x: -20 (RTL-source-order: button is at physical-left) */}
      <AnimatePresence mode="popLayout">
        {isExpanded && (
          <motion.div
            key="cancel-button"
            className="shrink-0"
            initial={{ opacity: 0, x: -20, filter: "blur(4px)" }}
            animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, x: -20, filter: "blur(4px)" }}
            transition={SEARCH_MOTION_TRANSITION}
          >
            <Button variant="light" onClick={onCancel}>
              {t("cancel")}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
